import {businessZone} from './utils/businessTime';
import {bookingScheduledAtToDate} from './utils/dateUtils';
// src/CalificarServicios.js
// Source of truth for /calificar-servicios.
// Do not keep a second rating screen with different logic; App.js renders this component.

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit as fbLimit,
  query,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import firebaseApp, { db as dbDefault, auth, firebaseConfigRuntime } from "./firebase";
import fsClient from "./data/firestoreClient";
import { httpRequestJson, isIosCapacitor } from "./utils/nativeHttp";
import { getManualAuth, isManualAuthLikelyValid } from "./utils/manualAuth";

import { callCallableNativeSafe } from "./utils/callableNativeSafe";
import { loadRatingBookings } from "./utils/loadRatingBookings";

function StarsPicker({ value, onChange }) {
  return (
    <div className="rating-stars-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          className={`rating-star-btn ${value >= n ? "active" : ""}`}
          type="button"
          aria-label={`${n} estrellas`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function getBookingDateObject(item) {
  const canonical=bookingScheduledAtToDate(item); if(canonical)return canonical;
  try {
    const sa = item.scheduledAt || item.scheduled_at || item.scheduledDate || item.scheduled || item.startAt || item.startTime || null;
    if (sa && typeof sa.toDate === "function") return sa.toDate();
    if (sa && sa.seconds) return new Date(sa.seconds * 1000);
    if (typeof sa === "string") {
      const d = new Date(sa);
      if (!Number.isNaN(d.getTime())) return d;
    }

    if (item.date) {
      let iso = String(item.date);
      if (item.time) {
        let t = String(item.time).trim();
        if (/^\d{1,2}:\d{2}$/.test(t)) t = `${t}:00`;
        iso = `${iso}T${t}`;
      } else {
        iso = `${iso}T00:00:00`;
      }
      const d = new Date(iso);
      if (!Number.isNaN(d.getTime())) return d;
    }
  } catch (_) {}
  return null;
}

function formatBookingDate(item) {
  const d = getBookingDateObject(item);
  if (!d) return "";
  try {
    return d.toLocaleString(item.locale || "es-PA", {timeZone:businessZone(item),
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return d.toLocaleString();
  }
}

function extractBusinessIdFromPath(path) {
  const text = String(path || "");
  const parts = text.split("/").filter(Boolean);
  const idx = parts.indexOf("businesses");
  if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
  return null;
}

function normalizeStatus(value) {
  return String(value || "").toLowerCase().trim();
}

const RATE_UNLOCK_DELAY_MS = 30 * 60 * 1000;
const RATE_EXPIRES_AFTER_MS = 7 * 24 * 60 * 60 * 1000;


const RATING_CACHE_TTL_MS = 90 * 1000;
const RATING_QUERY_TIMEOUT_MS = 6500;
const RATING_IOS_QUERY_TIMEOUT_MS = 4200;

function isRatingNativeIos() {
  try {
    return isIosCapacitor() === true;
  } catch (_) {
    return false;
  }
}

function ratingTimeoutFallback(label, fallback) {
  try {
    console.warn(`[CalificarServicios] timeout/fallback: ${label}`);
  } catch (_) {}
  return fallback;
}

function withRatingTimeout(promise, timeoutMs = RATING_QUERY_TIMEOUT_MS, fallback = null, label = "rating-task") {
  let timer = null;
  return Promise.race([
    Promise.resolve(promise).catch((error) => {
      try { console.warn(`[CalificarServicios] ${label} failed`, error); } catch (_) {}
      return fallback;
    }),
    new Promise((resolve) => {
      timer = setTimeout(() => resolve(ratingTimeoutFallback(label, fallback)), timeoutMs);
    }),
  ]).finally(() => {
    try { if (timer) clearTimeout(timer); } catch (_) {}
  });
}

function getRatingRouteHint() {
  try {
    if (typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search || "");
    return {
      bookingId: String(params.get("bookingId") || params.get("reservationId") || params.get("appointmentId") || "").trim(),
      businessId: String(params.get("businessId") || params.get("bizId") || params.get("business") || "").trim(),
      source: String(params.get("source") || "").trim(),
    };
  } catch (_) {
    return {};
  }
}


function getRatingFunctionsProjectId() {
  try {
    if (firebaseConfigRuntime?.projectId) return firebaseConfigRuntime.projectId;
  } catch (_) {}
  try {
    const cfg = typeof window !== "undefined" ? window.__FIREBASE_CONFIG__ : null;
    if (cfg?.projectId) return cfg.projectId;
  } catch (_) {}
  return "web-app-d9799";
}

function getSubmitRatingHttpUrl() {
  return `https://us-central1-${getRatingFunctionsProjectId()}.cloudfunctions.net/submitRatingHttp`;
}

function getRatingManualAuthToken() {
  try {
    const ma = getManualAuth?.();
    if (ma && (!isManualAuthLikelyValid || isManualAuthLikelyValid(ma)) && ma.idToken) return ma.idToken;
  } catch (_) {}
  try {
    const raw = localStorage.getItem("__MANUAL_AUTH__");
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed?.idToken) return parsed.idToken;
  } catch (_) {}
  return "";
}

function normalizeSubmitRatingHttpError(error) {
  try {
    const text = String(error?.message || error || "");
    const jsonMatch = text.match(/HTTP\s+\d+:\s*(\{.*\})/s);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[1]);
      const msg = parsed?.error?.message || parsed?.message || parsed?.error || "";
      const code = parsed?.error?.code || parsed?.code || "";
      if (msg) return code ? `${msg} (${code})` : msg;
    }
    return text || "No pudimos enviar la valoración.";
  } catch (_) {
    return error?.message || "No pudimos enviar la valoración.";
  }
}

async function submitRatingHttpIos(payload) {
  const token = getRatingManualAuthToken();
  if (!token) throw new Error("No encontramos sesión activa de Apple para enviar la valoración.");
  return httpRequestJson({
    url: getSubmitRatingHttpUrl(),
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });
}

function normalizeFsDocResult(result, path = "", label = "fs-doc", businessId = "") {
  try {
    if (!result) return null;
    if (typeof result.exists === "function" && !result.exists()) return null;
    if (result.exists === false) return null;
    const data = typeof result.data === "function"
      ? result.data()
      : result.data && typeof result.data === "object"
        ? result.data
        : result && typeof result === "object"
          ? result
          : {};
    if (!data || typeof data !== "object") return null;
    const id = String(result.id || data.id || data.bookingId || path.split("/").filter(Boolean).pop() || "").trim();
    return {
      id,
      _path: String(result.path || result._path || path || "").trim(),
      __scanSource: label,
      ...(businessId ? { businessId: String(businessId) } : {}),
      ...(data || {}),
    };
  } catch (_) {
    return null;
  }
}


function getRatingCacheKey(user, suffix = "bookings") {
  const uid = String(user?.uid || "").trim();
  const email = normalizeRatingEmail(user?.email || "");
  if (!uid && !email) return "";
  return `__LOOP_RATINGS_V2__:${suffix}:${uid || email}`;
}

function readRatingCache(key) {
  if (!key || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.createdAt) return null;
    if (Date.now() - Number(parsed.createdAt) > RATING_CACHE_TTL_MS) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeRatingCache(key, payload) {
  if (!key || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify({
      ...(payload || {}),
      createdAt: Date.now(),
    }));
  } catch (_) {}
}

function getBookingDedupKey(item = {}) {
  return item._path || `${getBookingBusinessId(item) || item.businessId || ""}:${item.id || item.bookingId || ""}`;
}

function mergeBookingsByKey(...groups) {
  const map = new Map();
  groups.flat().filter(Boolean).forEach((booking) => {
    const key = getBookingDedupKey(booking);
    if (!key || key === ":") return;
    const prev = map.get(key) || {};
    map.set(key, { ...prev, ...booking });
  });
  return Array.from(map.values());
}

function summarizeRatingBookingForDebug(b = {}) {
  const eligibility = getRatingEligibilityDebug(b);
  return {
    id: b.id || b.bookingId,
    businessId: getBookingBusinessId(b),
    customerEmail: b.customerEmail || b.userEmail || b.clientEmail || b.customerSnapshot?.email || b.customer?.email || "",
    status: b.status || "",
    bookingStatus: b.bookingStatus || b.bookingLifecycle?.bookingStatus || "",
    ratingStatus: getRatingBookingStatus(b),
    attendanceStatus: b.attendanceStatus || b.customerAttendanceStatus || b.bookingLifecycle?.attendanceStatus || "",
    scheduledAt: b.scheduledAt || b.startAt || b.date || "",
    startAt: b.startAt || "",
    endAt: b.endAt || "",
    durationMinutes: b.durationMinutes || b.duration || "",
    eligible: eligibility.eligible,
    reason: eligibility.reason,
    ratingSubmitted: b.ratingSubmitted === true,
    rated: b.rated === true,
    hasRating: b.hasRating === true,
    reviewRequestSentAt: Boolean(b.reviewRequestSentAt),
    scanSource: b.__scanSource || "",
  };
}

function getBookingEndDateObject(item) {
  const explicitEnd = item.endAt || item.endTime || null;
  if (explicitEnd && typeof explicitEnd.toDate === "function") return explicitEnd.toDate();
  if (explicitEnd && explicitEnd.seconds) return new Date(explicitEnd.seconds * 1000);
  if (typeof explicitEnd === "string") {
    const parsedEnd = new Date(explicitEnd);
    if (!Number.isNaN(parsedEnd.getTime())) return parsedEnd;
  }

  const start = getBookingDateObject(item);
  if (!start) return null;
  const durationMinutes = Number(item.durationMinutes || item.duration || item.serviceDuration || 60) || 60;
  return new Date(start.getTime() + durationMinutes * 60 * 1000);
}

function getRatingWindow(item = {}) {
  const end = getBookingEndDateObject(item) || getBookingDateObject({ scheduledAt: item.completedAt || item.markedAt || item.attendedCountedAt });
  if (!end) return { eligibleAt: null, expiresAt: null, available: false, expired: false, msLeft: 0 };
  const eligibleAt = new Date(end.getTime() + RATE_UNLOCK_DELAY_MS);
  const expiresAt = new Date(end.getTime() + RATE_EXPIRES_AFTER_MS);
  const now = Date.now();
  return {
    eligibleAt,
    expiresAt,
    available: now >= eligibleAt.getTime() && now <= expiresAt.getTime(),
    expired: now > expiresAt.getTime(),
    msLeft: Math.max(0, expiresAt.getTime() - now),
  };
}

function formatRatingWindow(item = {}) {
  const win = getRatingWindow(item);
  if (!win.expiresAt) return "";
  if (win.expired) return "Expiró";
  const days = Math.max(1, Math.ceil(win.msLeft / (24 * 60 * 60 * 1000)));
  return `Disponible ${days === 1 ? "hoy" : `por ${days} días`}`;
}

function ratingBusinessName(item = {}) {
  return item.businessName || item.__businessName || "Negocio";
}

function ratingServiceName(item = {}) {
  return item.serviceName || item.serviceTitle || item.service || item.title || "Servicio";
}

function ratingCleanText(value = "") {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (typeof value === "object") {
    const parts = [
      value.label,
      value.name,
      value.title,
      value.area,
      value.city,
      value.district,
      value.neighborhood,
      value.addressLine,
      value.address,
      value.formattedAddress,
      value.description,
    ].filter((x) => typeof x === "string" && x.trim());
    return parts[0] || "";
  }
  return "";
}

function ratingFirstLetter(value = "") {
  const text = String(value || "").trim();
  return text ? text.charAt(0).toUpperCase() : "L";
}

function ratingPickLogoFrom(source = {}) {
  if (!source || typeof source !== "object") return "";
  return (
    source.logoUrl ||
    source.logoURL ||
    source.logo ||
    source.businessLogo ||
    source.businessLogoUrl ||
    source.profileLogoUrl ||
    source.imageLogoUrl ||
    source.brandLogoUrl ||
    source.publicLogoUrl ||
    source.photoURL ||
    source.photoUrl ||
    source.avatarUrl ||
    source.coverLogoUrl ||
    source.imageUrl ||
    source.image ||
    source.mainImageUrl ||
    source.profileImageUrl ||
    source.coverImageUrl ||
    source.coverUrl ||
    source?.business?.logoUrl ||
    source?.business?.logoURL ||
    source?.business?.logo ||
    source?.business?.photoURL ||
    source?.business?.imageUrl ||
    ""
  );
}

function ratingBusinessLogo(item = {}) {
  return ratingPickLogoFrom(item) || item.__businessLogo || ratingPickLogoFrom(item.__businessProfile || {});
}

function ratingBusinessArea(item = {}) {
  const profile = item.__businessProfile && typeof item.__businessProfile === "object" ? item.__businessProfile : {};
  return (
    item.__businessArea ||
    ratingCleanText(profile.location) ||
    ratingCleanText(profile.address) ||
    ratingCleanText(profile.city) ||
    ratingCleanText(profile.area) ||
    ratingCleanText(item.businessLocation) ||
    ratingCleanText(item.location) ||
    ratingCleanText(item.address) ||
    ""
  );
}

function ratingDurationLabel(item = {}) {
  const n = Number(item.durationMinutes || item.duration || item.serviceDuration || item.serviceDurationMinutes || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${Math.round(n)} min`;
}

function ratingProfessionalName(item = {}) {
  return ratingCleanText(item.employeeName || item.workerName || item.staffName || item.professionalName || item.employeeId || item.workerId || "");
}

function RatingBusinessMediaMark({ item, businessName, modal = false }) {
  const logo = ratingBusinessLogo(item);
  const initial = ratingFirstLetter(businessName || ratingBusinessName(item));
  return (
    <div className={`rating-business-media-mark${modal ? " is-modal" : ""}`} aria-hidden="true">
      {logo ? (
        <img
          src={logo}
          alt=""
          loading="lazy"
          onError={(e) => {
            try {
              e.currentTarget.style.display = "none";
              e.currentTarget.parentElement?.classList.add("rating-logo-failed");
            } catch (_) {}
          }}
        />
      ) : null}
      <span className="rating-business-initial">{initial}</span>
    </div>
  );
}

function normalizeRatingEmail(value = "") {
  return String(value || "").trim().toLowerCase();
}

function bookingEmailCandidates(item = {}) {
  const snapshot = item.customerSnapshot && typeof item.customerSnapshot === "object" ? item.customerSnapshot : {};
  const customer = item.customer && typeof item.customer === "object" ? item.customer : {};
  return [
    item.userEmail,
    item.customerEmail,
    item.clientEmail,
    item.email,
    snapshot.email,
    snapshot.userEmail,
    snapshot.customerEmail,
    customer.email,
  ].map(normalizeRatingEmail).filter(Boolean);
}

function bookingUidCandidates(item = {}) {
  const snapshot = item.customerSnapshot && typeof item.customerSnapshot === "object" ? item.customerSnapshot : {};
  const customer = item.customer && typeof item.customer === "object" ? item.customer : {};
  return [
    item.userId,
    item.userUid,
    item.customerId,
    item.customerUid,
    item.clientUid,
    item.customerUserId,
    item.customerUserUid,
    item.linkedUserId,
    item.linkedUserUid,
    item.bookedBy,
    snapshot.uid,
    snapshot.userId,
    snapshot.userUid,
    snapshot.linkedUserId,
    snapshot.linkedUserUid,
    customer.uid,
    customer.userId,
    customer.userUid,
  ].map((x) => String(x || "").trim()).filter(Boolean);
}


function decodeRatingJwtPayload(token) {
  try {
    if (!token || typeof token !== "string") return null;
    const part = token.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, "=");
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
}

function getManualRatingAuthUser() {
  try {
    const parsed = (() => {
      try {
        const ma = getManualAuth?.();
        if (ma && (!isManualAuthLikelyValid || isManualAuthLikelyValid(ma))) return ma;
      } catch (_) {}
      try {
        const raw = localStorage.getItem("__MANUAL_AUTH__");
        return raw ? JSON.parse(raw) : null;
      } catch (_) {
        return null;
      }
    })();

    if (!parsed) return null;
    const payload = decodeRatingJwtPayload(parsed?.idToken || parsed?.token || "") || {};
    const uid = String(
      parsed?.uid ||
      parsed?.user?.uid ||
      payload?.user_id ||
      payload?.uid ||
      payload?.sub ||
      ""
    ).trim();
    const email = String(
      parsed?.email ||
      parsed?.user?.email ||
      parsed?.profile?.email ||
      payload?.email ||
      payload?.firebase?.identities?.email?.[0] ||
      ""
    ).trim();
    if (!uid && !email) return null;
    return {
      uid,
      email,
      displayName: parsed?.displayName || parsed?.name || parsed?.user?.displayName || parsed?.profile?.name || payload?.name || "",
      source: parsed?.source || "manual-auth",
    };
  } catch (_) {
    return null;
  }
}

function mergeRatingUser(primary = null) {
  const manual = getManualRatingAuthUser();
  const base = primary || {};
  const uid = String(base?.uid || manual?.uid || "").trim();
  const email = String(base?.email || manual?.email || "").trim();
  if (!uid && !email) return null;
  return {
    ...base,
    uid,
    email,
    displayName: base?.displayName || manual?.displayName || "",
  };
}

function bookingMatchesRatingUser(item = {}, user = {}) {
  const uid = String(user?.uid || "").trim();
  const email = normalizeRatingEmail(user?.email || "");
  if (uid && bookingUidCandidates(item).includes(uid)) return true;
  if (email && bookingEmailCandidates(item).includes(email)) return true;
  return false;
}


function getRatingBookingStatus(item = {}) {
  const lifecycle = item.bookingLifecycle && typeof item.bookingLifecycle === "object" ? item.bookingLifecycle : {};
  const candidates = [
    item.bookingStatus,
    lifecycle.bookingStatus,
    item.confirmationStatus,
    item.customerConfirmationStatus,
    item.attendanceStatus,
    item.customerAttendanceStatus,
    lifecycle.attendanceStatus,
    item.status,
    item.state,
  ].map(normalizeStatus).filter(Boolean);

  const paymentOnly = new Set(["paid", "unpaid", "pending_payment", "payment_pending", "cash", "card"]);
  const hardRejected = new Set(["cancelled", "canceled", "cancelada", "rechazada", "rejected", "declined", "no_show", "noshow"]);
  const complete = new Set(["attended", "completed", "complete", "done", "finished", "finalizada", "finalizado"]);
  const allowed = new Set(["approved", "accepted", "confirmed", "confirmada", "confirmado", "aprobada", "aprobado", "assigned"]);

  const rejected = candidates.find((s) => hardRejected.has(s));
  if (rejected) return rejected;

  const done = candidates.find((s) => complete.has(s));
  if (done) return done;

  const scheduled = candidates.find((s) => allowed.has(s));
  if (scheduled) return scheduled;

  return candidates.find((s) => !paymentOnly.has(s)) || candidates[0] || "";
}

function getRatingEligibilityDebug(item = {}) {
  if (!item) return { eligible: false, reason: "missing-item" };
  if (item.ratingSubmitted || item.rated || item.hasRating) return { eligible: false, reason: "already-rated" };

  const status = getRatingBookingStatus(item);
  const hardRejectedStatuses = new Set(["cancelled", "canceled", "cancelada", "rechazada", "rejected", "declined", "no_show", "noshow"]);
  if (hardRejectedStatuses.has(status)) return { eligible: false, reason: `rejected:${status}` };

  const attendedFlag =
    status === "attended" ||
    status === "completed" ||
    status === "complete" ||
    status === "done" ||
    status === "finished" ||
    status === "finalizada" ||
    status === "finalizado" ||
    !!item.markedAt ||
    !!item.attendanceMarkedBy ||
    !!item.attendedCountedAt ||
    !!item.completedAt;

  const allowedScheduledStatuses = new Set(["approved", "accepted", "confirmed", "confirmada", "confirmado", "aprobada", "aprobado", "assigned"]);

  if (!attendedFlag && status && !allowedScheduledStatuses.has(status)) {
    return { eligible: false, reason: `bad-status:${status}` };
  }

  const win = getRatingWindow(item);
  if (!win.eligibleAt || !win.expiresAt) return { eligible: false, reason: "missing-date" };
  if (win.expired) return { eligible: false, reason: "expired", eligibleAt: win.eligibleAt.toISOString(), expiresAt: win.expiresAt.toISOString() };
  if (!win.available) return { eligible: false, reason: "not-yet-available", eligibleAt: win.eligibleAt.toISOString(), expiresAt: win.expiresAt.toISOString() };

  return { eligible: true, reason: "eligible", status, eligibleAt: win.eligibleAt.toISOString(), expiresAt: win.expiresAt.toISOString() };
}


function isBookingEligibleForRating(item) {
  return getRatingEligibilityDebug(item).eligible === true;
}

function getBookingBusinessId(item) {
  return item.businessId || item.businessID || item.ownerBusinessId || extractBusinessIdFromPath(item._path);
}

function getBookingUserName(user) {
  return (
    (user && user.displayName ? String(user.displayName).trim() : "") ||
    (user && user.email ? String(user.email).trim() : "") ||
    "Cliente"
  );
}

function RatingModal({ item, onClose, onSubmit, submitting }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const businessName = ratingBusinessName(item);
  const serviceName = ratingServiceName(item);
  const businessArea = ratingBusinessArea(item);
  const durationLabel = ratingDurationLabel(item);

  return (
    <div className="rating-modal-backdrop">
      <div className="rating-modal-card">
        <div className="rating-modal-kicker">Valoración pública</div>
        <h4>¿Cómo fue tu experiencia?</h4>
        <p>
          Tu valoración aparecerá en la página pública de {businessName} y ayudará a otros clientes a elegir mejor.
        </p>

        <div className="rating-modal-summary rating-modal-summary-brand">
          <RatingBusinessMediaMark item={item} businessName={businessName} modal />
          <div className="rating-modal-summary-copy">
            <strong>{businessName}</strong>
            <span>{serviceName} · {formatBookingDate(item)}</span>
            {businessArea ? <em>{businessArea}</em> : null}
            {durationLabel ? <small>{durationLabel}</small> : null}
          </div>
        </div>

        <StarsPicker value={rating} onChange={setRating} />

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Comentario opcional: ¿qué te gustó del servicio?"
          className="rating-textarea"
          rows={4}
          maxLength={900}
        />

        <div className="rating-modal-actions">
          <button
            type="button"
            onClick={() => onSubmit(item, rating, comment)}
            disabled={submitting}
            className="rating-primary-btn"
          >
            {submitting ? "Enviando…" : "Publicar valoración"}
          </button>

          <button type="button" onClick={onClose} className="rating-secondary-btn">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function RatingSkeletonList({ count = 4 }) {
  return (
    <div className="rating-list rating-skeleton-list" aria-hidden="true">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="rating-card rating-card-skeleton">
          <div className="rating-card-mark rating-skeleton-pulse" />
          <div className="rating-card-copy">
            <div className="rating-skeleton-line rating-skeleton-title" />
            <div className="rating-skeleton-line rating-skeleton-service" />
            <div className="rating-skeleton-line rating-skeleton-date" />
            <div className="rating-skeleton-pill" />
          </div>
          <div className="rating-skeleton-button" />
        </div>
      ))}
    </div>
  );
}

const styles = `
  .rating-screen {
    width: 100%;
    min-height: calc(100vh - 62px);
    position: relative;
    z-index: 0;
    isolation: isolate;
    background: transparent;
    padding: 1px 0 84px;
  }

  /* Full-canvas background fix:
     avoids the center/side double-tone created when parent wrappers have
     a different background than this route. */
  .rating-screen::before {
    content: "";
    position: fixed;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background: #fff6e9;
  }

  html:has(.rating-screen),
  body:has(.rating-screen),
  #root:has(.rating-screen) {
    min-height: 100%;
    background: #fff6e9;
  }

  .rating-page {
    max-width: 940px;
    margin: 0 auto;
    padding: 28px 18px 48px;
  }

  .rating-hero {
    position: relative;
    padding: 18px 4px 22px;
    margin-bottom: 18px;
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    overflow: visible;
  }

  .rating-hero::before {
    content: "";
    position: absolute;
    left: 4px;
    top: 0;
    width: 84px;
    height: 6px;
    border-radius: 999px;
    background: linear-gradient(90deg, #008878 0%, #6ee7d8 100%);
    box-shadow: 0 8px 20px rgba(0,136,120,.16);
  }

  .rating-hero::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background: linear-gradient(90deg, rgba(0,136,120,.26) 0%, rgba(203,213,225,.92) 48%, rgba(203,213,225,0) 100%);
  }

  .rating-kicker,
  .rating-modal-kicker {
    color: #008878;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: .16em;
    text-transform: uppercase;
    margin: 0 0 10px;
  }

  .rating-title {
    margin: 0;
    color: #0f172a;
    font-size: clamp(34px, 4vw, 48px);
    line-height: .98;
    letter-spacing: -.07em;
    font-weight: 950;
  }

  .rating-copy {
    margin: 12px 0 0;
    color: #64748b;
    font-size: 15px;
    line-height: 1.55;
    font-weight: 700;
    max-width: 700px;
  }

  .rating-state {
    border: 1px dashed #cbd5e1;
    border-radius: 22px;
    background: #fff;
    color: #64748b;
    padding: 18px;
    font-weight: 800;
  }

  .rating-refresh-notice {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    margin: 0 0 14px; padding: 10px 14px; border-radius: 12px;
    color: #655c49; background: #faf5e9; font-size: 13px; font-weight: 500;
  }
  .rating-refresh-notice button { flex-shrink: 0; padding: 7px 12px; font-size: 12px; }
  .rating-state.error {
    color: #b91c1c;
    border-color: #fecaca;
    background: #fef2f2;
  }

  .rating-skeleton-list {
    animation: ratingListWarmIn .22s ease both;
  }

  .rating-card-skeleton {
    pointer-events: none;
    min-height: 98px;
  }

  .rating-skeleton-pulse,
  .rating-skeleton-line,
  .rating-skeleton-pill,
  .rating-skeleton-button {
    background: linear-gradient(90deg, #edf5f2 0%, #f8fafc 42%, #edf5f2 82%);
    background-size: 220% 100%;
    animation: ratingSkeletonShimmer 1.05s ease-in-out infinite;
  }

  .rating-card-skeleton .rating-card-mark {
    color: transparent;
  }

  .rating-skeleton-line {
    height: 11px;
    border-radius: 999px;
    margin-bottom: 8px;
  }

  .rating-skeleton-title {
    width: 150px;
    height: 15px;
  }

  .rating-skeleton-service {
    width: 118px;
  }

  .rating-skeleton-date {
    width: 180px;
    opacity: .8;
  }

  .rating-skeleton-pill {
    width: 118px;
    height: 28px;
    border-radius: 999px;
    border: 1px solid rgba(226,232,240,.7);
    margin-top: 4px;
  }

  .rating-skeleton-button {
    width: 104px;
    height: 44px;
    border-radius: 16px;
    box-shadow: 0 14px 30px rgba(0,103,95,.08);
  }

  @keyframes ratingSkeletonShimmer {
    0% { background-position: 120% 0; }
    100% { background-position: -120% 0; }
  }

  @keyframes ratingListWarmIn {
    from {
      opacity: .72;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .rating-list {
    display: grid;
    gap: 14px;
  }

  .rating-list-ready {
    animation: ratingListWarmIn .18s ease both;
  }

  .rating-card {
    border: 1px solid rgba(226,232,240,.95);
    border-radius: 26px;
    background: #ffffff;
    box-shadow: 0 16px 44px rgba(15,23,42,.055);
    padding: 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
  }

  .rating-card-title {
    color: #0f172a;
    font-size: 17px;
    font-weight: 950;
    letter-spacing: -.025em;
  }

  .rating-card-service {
    color: #334155;
    font-size: 14px;
    font-weight: 850;
    margin-top: 4px;
  }

  .rating-card-date {
    color: #94a3b8;
    font-size: 12.5px;
    font-weight: 800;
    margin-top: 5px;
  }

  .rating-rate-btn,
  .rating-primary-btn {
    border: 0;
    border-radius: 16px;
    background: linear-gradient(135deg, #00675f, #008878);
    color: #ffffff;
    font-weight: 950;
    cursor: pointer;
    box-shadow: 0 14px 30px rgba(0,103,95,.18);
  }

  .rating-rate-btn {
    min-height: 44px;
    padding: 0 16px;
  }

  .rating-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 10080;
    background: rgba(15,23,42,.48);
    display: grid;
    place-items: center;
    padding: 18px;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }

  .rating-modal-card {
    width: min(100%, 520px);
    border-radius: 30px;
    border: 1px solid rgba(226,232,240,.95);
    background: #ffffff;
    box-shadow: 0 30px 90px rgba(15,23,42,.28);
    padding: 24px;
  }

  .rating-modal-card h4 {
    margin: 0;
    color: #0f172a;
    font-size: 28px;
    line-height: 1;
    letter-spacing: -.06em;
    font-weight: 950;
  }

  .rating-modal-card p {
    margin: 10px 0 18px;
    color: #64748b;
    font-size: 14px;
    line-height: 1.5;
    font-weight: 700;
  }

  .rating-stars-picker {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin: 8px 0 16px;
  }

  .rating-star-btn {
    width: 46px;
    height: 42px;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #cbd5e1;
    font-size: 20px;
    font-weight: 950;
    cursor: pointer;
  }

  .rating-star-btn.active {
    background: #fffbeb;
    border-color: #fde68a;
    color: #f59e0b;
  }

  .rating-textarea {
    width: 100%;
    border: 1px solid #dbe3ee;
    border-radius: 18px;
    padding: 14px;
    color: #0f172a;
    font-size: 14px;
    font-weight: 750;
    outline: none;
    resize: vertical;
    box-sizing: border-box;
  }

  .rating-textarea:focus {
    border-color: rgba(0,137,127,.45);
    box-shadow: 0 0 0 4px rgba(0,137,127,.10);
  }

  .rating-modal-actions {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;
    margin-top: 14px;
  }

  .rating-primary-btn {
    min-height: 48px;
    padding: 0 16px;
  }

  .rating-secondary-btn {
    min-height: 48px;
    padding: 0 16px;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #334155;
    font-weight: 900;
    cursor: pointer;
  }

  @media (max-width: 720px) {
    .rating-page {
      padding: 18px 12px calc(92px + env(safe-area-inset-bottom, 0px));
    }

    .rating-hero {
      padding: 20px;
      border-radius: 26px;
    }

    .rating-card {
      align-items: stretch;
      flex-direction: column;
      border-radius: 24px;
    }

    .rating-rate-btn {
      width: 100%;
    }

    .rating-modal-card {
      border-radius: 26px;
      padding: 20px;
    }

    .rating-modal-actions {
      grid-template-columns: 1fr;
    }
  }

  /* LOOP RATING UX POLISH */
  .rating-page {
    width: min(100%, 960px);
  }

  .rating-hero {
    background:
      radial-gradient(circle at 0% 0%, rgba(0,137,127,.12), transparent 34%),
      linear-gradient(135deg, #ffffff 0%, #f8fffd 100%);
  }

  .rating-list {
    gap: 12px;
  }

  .rating-card {
    padding: 14px 16px;
    border-radius: 24px;
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr) auto;
    align-items: center;
    gap: 13px;
    background:
      radial-gradient(circle at 0% 0%, rgba(20,184,166,.07), transparent 34%),
      #ffffff;
  }

  .rating-card-mark {
    width: 48px;
    height: 48px;
    border-radius: 17px;
    background: #ecfdf5;
    color: #00675f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    font-weight: 950;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.88);
  }

  .rating-card-copy {
    min-width: 0;
  }

  .rating-card-title {
    font-size: 16px;
    line-height: 1.1;
  }

  .rating-card-service {
    color: #00675f;
    font-size: 13px;
    margin-top: 4px;
  }

  .rating-card-date {
    margin-top: 5px;
  }

  .rating-card-window {
    display: inline-flex;
    width: fit-content;
    margin-top: 7px;
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #64748b;
    padding: 5px 8px;
    font-size: 11px;
    font-weight: 900;
  }

  .rating-rate-btn {
    min-width: 104px;
  }

  .rating-modal-summary {
    border: 1px solid rgba(226,232,240,.92);
    background: #f8fafc;
    border-radius: 18px;
    padding: 12px;
    margin: 0 0 14px;
  }

  .rating-modal-summary strong {
    display: block;
    color: #0f172a;
    font-size: 15px;
    line-height: 1.1;
    font-weight: 950;
  }

  .rating-modal-summary span {
    display: block;
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
    margin-top: 5px;
  }

  @media (max-width: 720px) {
    .rating-card {
      grid-template-columns: 48px minmax(0, 1fr);
      align-items: start;
    }

    .rating-rate-btn {
      grid-column: 1 / -1;
      width: 100%;
    }
  }
  @media (max-width: 720px) {
    .rating-screen {
      min-height: calc(100vh - 58px);
      padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px));
    }

    .rating-skeleton-button {
      grid-column: 1 / -1;
      width: 100%;
    }

    .rating-card-skeleton {
      min-height: 142px;
    }
  }


  /* LOOP RATING — PREMIUM CARD V3, aligned with MyBookings visual system */
  .rating-page {
    max-width: 980px;
  }

  .rating-card-premium,
  .rating-card-skeleton {
    position: relative;
    overflow: hidden;
    border-radius: 28px;
    padding: 16px;
    display: grid;
    grid-template-columns: 74px minmax(0, 1fr) auto;
    align-items: flex-start;
    gap: 12px;
    background:
      radial-gradient(circle at 0% 0%, rgba(20,184,166,.085), transparent 34%),
      linear-gradient(180deg, #ffffff 0%, #fffefe 100%);
    box-shadow: 0 16px 40px rgba(15,23,42,.065);
    transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
  }

  .rating-card-premium::before,
  .rating-card-skeleton::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(135deg, rgba(0,103,95,.055), transparent 44%);
    opacity: .78;
  }

  .rating-card-premium > *,
  .rating-card-skeleton > * {
    position: relative;
    z-index: 1;
  }

  .rating-card-premium:hover {
    transform: translateY(-2px);
    border-color: rgba(0,103,95,.22);
    box-shadow: 0 24px 56px rgba(15,23,42,.09);
  }

  .rating-business-media-mark {
    width: 74px;
    height: 46px;
    border-radius: 15px;
    border: 1px solid rgba(203,213,225,.78);
    background:
      radial-gradient(circle at 30% 18%, rgba(20,184,166,.10), transparent 48%),
      #f8fafc;
    box-shadow: 0 12px 24px rgba(15,23,42,.07);
    overflow: hidden;
    flex: 0 0 74px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .rating-business-media-mark img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
    object-position: center;
    padding: 5px;
    box-sizing: border-box;
    background: #ffffff;
  }

  .rating-business-media-mark img[src*="cover"],
  .rating-business-media-mark img[src*="photo"],
  .rating-business-media-mark img[src*="image"] {
    object-fit: cover;
    padding: 0;
    background: transparent;
  }

  .rating-business-initial {
    color: #00675f;
    font-size: 22px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: -.08em;
    text-transform: uppercase;
  }

  .rating-business-media-mark:not(.rating-logo-failed) img + .rating-business-initial {
    display: none;
  }

  .rating-card-main {
    min-width: 0;
  }

  .rating-card-title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }

  .rating-card-title-stack {
    min-width: 0;
    display: grid;
    gap: 2px;
  }

  .rating-card-title {
    margin: 0;
    color: #0f172a;
    font-size: 18px;
    line-height: 1.06;
    font-weight: 950;
    letter-spacing: -.045em;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rating-card-service {
    margin: 0;
    color: #00675f;
    font-size: 12.5px;
    line-height: 1.15;
    font-weight: 900;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rating-business-area {
    margin: 2px 0 0;
    color: #94a3b8;
    font-size: 11px;
    line-height: 1.2;
    font-weight: 850;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rating-status-pill {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 26px;
    padding: 0 10px;
    border-radius: 999px;
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #bbf7d0;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .rating-card-info-row {
    margin-top: 7px;
    display: flex;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
  }

  .rating-card-duration,
  .rating-card-window {
    display: inline-flex;
    width: fit-content;
    max-width: 100%;
    align-items: center;
    min-height: 24px;
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #64748b;
    padding: 0 8px;
    font-size: 11.5px;
    line-height: 1;
    font-weight: 900;
    margin-top: 0;
  }

  .rating-card-meta-grid {
    margin-top: 11px;
    border-top: 1px solid rgba(226,232,240,.72);
    padding-top: 11px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px 14px;
  }

  .rating-card-meta-grid div {
    min-width: 0;
  }

  .rating-card-meta-grid span {
    display: block;
    color: #94a3b8;
    font-size: 10px;
    font-weight: 950;
    letter-spacing: .08em;
    text-transform: uppercase;
    margin-bottom: 3px;
  }

  .rating-card-meta-grid strong {
    display: block;
    color: #334155;
    font-size: 12.5px;
    line-height: 1.25;
    font-weight: 850;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rating-card-actions {
    align-self: center;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .rating-rate-btn {
    min-width: 110px;
    min-height: 44px;
    border-radius: 16px;
    padding: 0 16px;
    transition: transform 140ms ease, box-shadow 140ms ease, background 140ms ease;
  }

  .rating-rate-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 34px rgba(0,103,95,.22);
  }

  .rating-modal-summary-brand {
    display: grid;
    grid-template-columns: 74px minmax(0, 1fr);
    align-items: center;
    gap: 12px;
  }

  .rating-business-media-mark.is-modal {
    width: 74px;
    height: 50px;
    flex-basis: 74px;
  }

  .rating-modal-summary-copy {
    min-width: 0;
  }

  .rating-modal-summary-copy em,
  .rating-modal-summary-copy small {
    display: inline-flex;
    width: fit-content;
    max-width: 100%;
    margin: 7px 7px 0 0;
    font-style: normal;
    border-radius: 999px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    padding: 5px 8px;
    font-size: 11px;
    line-height: 1;
    font-weight: 900;
  }

  .rating-card-skeleton {
    min-height: 144px;
  }

  .rating-card-skeleton .rating-card-mark {
    width: 74px;
    height: 46px;
    border-radius: 15px;
    flex: 0 0 74px;
  }

  .rating-card-skeleton .rating-card-copy {
    padding-top: 2px;
  }

  @media (max-width: 720px) {
    .rating-card-premium,
    .rating-card-skeleton {
      grid-template-columns: 66px minmax(0, 1fr);
      gap: 12px;
      padding: 15px;
      border-radius: 24px;
    }

    .rating-business-media-mark,
    .rating-card-skeleton .rating-card-mark {
      width: 66px;
      height: 44px;
      flex-basis: 66px;
      border-radius: 14px;
    }

    .rating-card-actions,
    .rating-skeleton-button {
      grid-column: 1 / -1;
      width: 100%;
    }

    .rating-rate-btn {
      width: 100%;
    }

    .rating-card-title-row {
      display: grid;
      gap: 8px;
    }

    .rating-status-pill {
      width: fit-content;
    }

    .rating-card-meta-grid {
      grid-template-columns: 1fr 1fr;
    }

    .rating-modal-summary-brand {
      grid-template-columns: 66px minmax(0, 1fr);
    }

    .rating-business-media-mark.is-modal {
      width: 66px;
      height: 46px;
      flex-basis: 66px;
    }
  }

  @media (max-width: 420px) {
    .rating-card-meta-grid {
      grid-template-columns: 1fr;
    }
  }


  /* LOOP RATING SCREEN — MOBILE/TABLET FIT V10 */
  @media (max-width: 920px) {
    body:not(.is-mobile-app) .rating-screen {
      margin-top: calc(-1 * var(--loop-global-header-mobile-h, 58px)) !important;
      min-height: calc(100dvh - var(--loop-global-header-mobile-h, 58px)) !important;
      padding-top: 0 !important;
    }

    body.is-mobile-app .rating-screen {
      margin-top: calc(-1 * var(--loop-mobile-header-total, var(--loop-native-header-h, var(--loop-global-header-mobile-h, 88px)))) !important;
      min-height: calc(100dvh - var(--loop-mobile-header-total, var(--loop-native-header-h, var(--loop-global-header-mobile-h, 88px)))) !important;
      padding-top: 0 !important;
    }

    .rating-page {
      width: min(100%, 720px) !important;
      padding-top: 8px !important;
      padding-left: 14px !important;
      padding-right: 14px !important;
    }

    .rating-hero {
      padding: 14px 0 18px !important;
      margin-bottom: 14px !important;
    }

    .rating-hero::before {
      left: 0 !important;
      width: 72px !important;
      height: 5px !important;
    }

    .rating-hero::after {
      left: 0 !important;
      right: 0 !important;
    }

    .rating-kicker {
      margin-bottom: 7px !important;
      font-size: 10px !important;
      letter-spacing: .15em !important;
    }

    .rating-title {
      font-size: clamp(29px, 7vw, 38px) !important;
      letter-spacing: -.072em !important;
    }

    .rating-copy {
      margin-top: 9px !important;
      font-size: 13.5px !important;
      line-height: 1.45 !important;
      max-width: 520px !important;
    }

    .rating-list {
      gap: 10px !important;
    }

    .rating-card-premium,
    .rating-card-skeleton {
      grid-template-columns: 66px minmax(0, 1fr) auto !important;
      gap: 11px !important;
      align-items: center !important;
      border-radius: 23px !important;
      padding: 13px !important;
      min-height: 104px !important;
      box-shadow: 0 13px 32px rgba(15,23,42,.055) !important;
    }

    .rating-business-media-mark,
    .rating-card-skeleton .rating-card-mark {
      width: 66px !important;
      height: 44px !important;
      flex-basis: 66px !important;
      border-radius: 14px !important;
      align-self: center !important;
    }

    .rating-card-main,
    .rating-card-copy {
      min-width: 0 !important;
    }

    .rating-card-title-row {
      display: block !important;
      min-width: 0 !important;
    }

    .rating-card-title-stack {
      min-width: 0 !important;
      display: grid !important;
      gap: 2px !important;
    }

    .rating-card-title {
      font-size: 15.5px !important;
      line-height: 1.05 !important;
      letter-spacing: -.045em !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .rating-card-service {
      font-size: 12px !important;
      line-height: 1.05 !important;
      margin: 0 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .rating-business-area {
      margin-top: 3px !important;
      font-size: 10.5px !important;
      line-height: 1.1 !important;
      max-width: 100% !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .rating-status-pill {
      display: none !important;
    }

    .rating-card-info-row {
      margin-top: 7px !important;
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: center !important;
      gap: 5px !important;
      max-height: 44px !important;
      overflow: hidden !important;
    }

    .rating-card-date-chip,
    .rating-card-duration,
    .rating-card-window {
      min-height: 20px !important;
      height: 20px !important;
      max-height: 20px !important;
      margin: 0 !important;
      padding: 0 7px !important;
      border-radius: 999px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 10px !important;
      line-height: 1 !important;
      font-weight: 900 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .rating-card-date-chip {
      max-width: 118px !important;
      background: #fff7ed !important;
      color: #9a3412 !important;
      border: 1px solid #fed7aa !important;
    }

    .rating-card-duration {
      max-width: 64px !important;
    }

    .rating-card-window {
      max-width: 118px !important;
    }

    .rating-card-meta-grid {
      display: none !important;
    }

    .rating-card-actions,
    .rating-skeleton-button {
      grid-column: 3 / 4 !important;
      grid-row: 1 / 2 !important;
      width: auto !important;
      align-self: center !important;
      justify-self: end !important;
    }

    .rating-rate-btn {
      width: auto !important;
      min-width: 76px !important;
      max-width: 92px !important;
      min-height: 34px !important;
      height: 34px !important;
      padding: 0 12px !important;
      border-radius: 999px !important;
      font-size: 12px !important;
      line-height: 1 !important;
      box-shadow: 0 10px 22px rgba(0,103,95,.18) !important;
    }
  }

  @media (max-width: 430px) {
    .rating-page {
      padding: 6px 12px calc(70px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .rating-hero {
      padding: 12px 0 16px !important;
    }

    .rating-hero::before {
      width: 64px !important;
      height: 4px !important;
    }

    .rating-title {
      font-size: 30px !important;
    }

    .rating-copy {
      font-size: 13px !important;
      line-height: 1.42 !important;
    }

    .rating-card-premium,
    .rating-card-skeleton {
      grid-template-columns: 58px minmax(0, 1fr) auto !important;
      gap: 9px !important;
      padding: 12px !important;
      min-height: 98px !important;
      border-radius: 22px !important;
    }

    .rating-business-media-mark,
    .rating-card-skeleton .rating-card-mark {
      width: 58px !important;
      height: 40px !important;
      flex-basis: 58px !important;
      border-radius: 13px !important;
    }

    .rating-business-media-mark img {
      padding: 3px !important;
    }

    .rating-business-media-mark img[src*="cover"],
    .rating-business-media-mark img[src*="photo"],
    .rating-business-media-mark img[src*="image"] {
      padding: 0 !important;
    }

    .rating-card-title {
      font-size: 14.6px !important;
    }

    .rating-card-service {
      font-size: 11.5px !important;
    }

    .rating-business-area {
      font-size: 10px !important;
    }

    .rating-card-date-chip,
    .rating-card-duration,
    .rating-card-window {
      min-height: 18px !important;
      height: 18px !important;
      max-height: 18px !important;
      padding: 0 6px !important;
      font-size: 9.2px !important;
    }

    .rating-card-date-chip {
      max-width: 96px !important;
    }

    .rating-card-window {
      max-width: 96px !important;
    }

    .rating-rate-btn {
      min-width: 70px !important;
      max-width: 78px !important;
      min-height: 32px !important;
      height: 32px !important;
      padding: 0 10px !important;
      font-size: 11.2px !important;
    }
  }

  /* LOOP RATING TITLE — BUSINESS PAGE STYLE V12 */
  .rating-page {
    width: min(100%, 1120px) !important;
    padding-top: 18px !important;
  }

  .rating-hero {
    background: transparent !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    padding: 6px 0 14px !important;
    margin-bottom: 18px !important;
  }

  .rating-hero::before {
    left: 0 !important;
    top: 0 !important;
    width: 86px !important;
    height: 5px !important;
    border-radius: 999px !important;
    background: linear-gradient(90deg, #0ea5a0 0%, #67e8f9 100%) !important;
    box-shadow: none !important;
  }

  .rating-hero::after {
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    height: 1px !important;
    background: rgba(203, 213, 225, .92) !important;
  }

  .rating-kicker,
  .rating-modal-kicker {
    margin: 16px 0 8px !important;
    font-size: 11px !important;
    letter-spacing: .18em !important;
  }

  .rating-title {
    font-size: clamp(34px, 4.8vw, 58px) !important;
    line-height: .94 !important;
    letter-spacing: -.075em !important;
    max-width: 720px !important;
  }

  .rating-copy {
    margin-top: 10px !important;
    max-width: 780px !important;
    font-size: 15px !important;
    line-height: 1.5 !important;
  }

  @media (max-width: 920px) {
    .rating-page {
      padding-top: 10px !important;
    }

    .rating-hero {
      padding: 4px 0 14px !important;
      margin-bottom: 14px !important;
    }

    .rating-kicker,
    .rating-modal-kicker {
      margin-top: 14px !important;
      margin-bottom: 8px !important;
    }

    .rating-title {
      font-size: clamp(32px, 10vw, 46px) !important;
      max-width: 100% !important;
    }

    .rating-copy {
      max-width: 100% !important;
    }
  }

  @media (max-width: 430px) {
    .rating-hero::before {
      width: 68px !important;
      height: 4px !important;
    }

    .rating-kicker,
    .rating-modal-kicker {
      font-size: 10px !important;
      letter-spacing: .16em !important;
    }

    .rating-title {
      font-size: 30px !important;
    }

    .rating-copy {
      font-size: 13px !important;
      line-height: 1.48 !important;
    }
  }


  /* LOOP RATING — IOS/NATIVE SAFE FIT V11 */
  body.is-mobile-app .rating-screen {
    margin-top: 0 !important;
    min-height: calc(100dvh - var(--loop-mobile-header-total, var(--loop-native-header-h, var(--loop-global-header-mobile-h, 88px)))) !important;
    padding-top: max(10px, env(safe-area-inset-top, 0px)) !important;
    padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important;
    overflow-x: hidden !important;
  }

  body.is-mobile-app .rating-page {
    width: min(100%, 980px) !important;
    padding-top: clamp(10px, 2.2vw, 24px) !important;
    padding-left: clamp(14px, 3.8vw, 38px) !important;
    padding-right: clamp(14px, 3.8vw, 38px) !important;
  }

  body.is-mobile-app .rating-hero {
    padding-top: 14px !important;
    overflow: visible !important;
  }

  body.is-mobile-app .rating-title {
    line-height: 1.02 !important;
    overflow: visible !important;
    text-wrap: balance !important;
  }

  @media (pointer: coarse) and (min-width: 721px) and (max-width: 1368px) {
    body.is-mobile-app .rating-screen {
      padding-top: max(16px, env(safe-area-inset-top, 0px)) !important;
    }

    body.is-mobile-app .rating-page {
      width: min(100%, 980px) !important;
      padding-top: 24px !important;
    }

    body.is-mobile-app .rating-title {
      font-size: clamp(42px, 5.6vw, 58px) !important;
    }
  }


`;

export default function CalificarServicios({ db: dbProp, user: userProp }) {
  const dbUsed = dbProp || dbDefault;

  // LOOP_RATING_HEADER_CLEARANCE_OWNER_V58
  // Web mobile only: align the rating route to the REAL bottom of the fixed header.
  // This retires the visual effect of the old -58px pull-up without touching native.
  React.useLayoutEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;
    if (document.body.classList.contains("is-mobile-app")) return undefined;

    const media = window.matchMedia("(max-width: 920px)");
    if (!media.matches) return undefined;

    let rafId = 0;
    let resizeObserver = null;

    const alignRoute = () => {
      const page = document.querySelector(".rating-screen");
      const header = document.querySelector("#app-main-header, .loop-ref-header, .loop-mobile-header-v2");
      if (!page || !header || !page.isConnected || !header.isConnected) return;

      const headerRect = header.getBoundingClientRect();
      const pageRect = page.getBoundingClientRect();
      if (!Number.isFinite(headerRect.bottom) || !Number.isFinite(pageRect.top)) return;

      const targetTop = Math.ceil(headerRect.bottom);
      const delta = targetTop - pageRect.top;
      const currentMargin = Number.parseFloat(window.getComputedStyle(page).marginTop || "0") || 0;

      if (Math.abs(delta) > 0.5) {
        const nextMargin = Math.round((currentMargin + delta) * 10) / 10;
        page.style.setProperty("margin-top", `${nextMargin}px`, "important");
      }

      page.dataset.loopHeaderClearanceOwner = "V58-rating";
      page.dataset.loopHeaderBottom = String(targetTop);
    };

    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(alignRoute);
    };

    alignRoute();
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    try {
      if (typeof ResizeObserver !== "undefined") {
        const header = document.querySelector("#app-main-header, .loop-ref-header, .loop-mobile-header-v2");
        if (header) {
          resizeObserver = new ResizeObserver(schedule);
          resizeObserver.observe(header);
        }
      }
    } catch (_) {}

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      try { resizeObserver?.disconnect?.(); } catch (_) {}
      const page = document.querySelector(".rating-screen");
      if (page?.dataset?.loopHeaderClearanceOwner === "V58-rating") {
        try { page.style.removeProperty("margin-top"); } catch (_) {}
      }
    };
  }, []);
  const [manualAuthTick, setManualAuthTick] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const refresh = () => setManualAuthTick((x) => x + 1);
    window.addEventListener("manual-auth-changed", refresh);
    window.addEventListener("manual-auth-detected", refresh);
    window.addEventListener("loop-auth-login-success", refresh);
    window.addEventListener("loop-auth-session-changed", refresh);
    window.addEventListener("loop-authenticated", refresh);
    refresh();
    return () => {
      window.removeEventListener("manual-auth-changed", refresh);
      window.removeEventListener("manual-auth-detected", refresh);
      window.removeEventListener("loop-auth-login-success", refresh);
      window.removeEventListener("loop-auth-session-changed", refresh);
      window.removeEventListener("loop-authenticated", refresh);
    };
  }, []);

  const rawRatingUser = userProp || (auth && auth.currentUser);
  const user = useMemo(
    () => mergeRatingUser(rawRatingUser),
    [
      rawRatingUser?.uid,
      rawRatingUser?.email,
      rawRatingUser?.displayName,
      manualAuthTick,
    ]
  );
  const routeHint = useMemo(() => getRatingRouteHint(), []);
  const isIosNative = isRatingNativeIos();

  const bookingCacheKey = useMemo(() => getRatingCacheKey(user, "bookings"), [user?.uid, user?.email]);
  const ratedCacheKey = useMemo(() => getRatingCacheKey(user, "rated"), [user?.uid, user?.email]);

  const [userBookings, setUserBookings] = useState([]);
  const [bookingsError, setBookingsError] = useState(null);
  const [bookingRetry, setBookingRetry] = useState(0);
  const [refreshingBookings, setRefreshingBookings] = useState(false);
  const [ratedBookingIds, setRatedBookingIds] = useState(new Set());
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [loadingRatings, setLoadingRatings] = useState(true);

  const [modalItem, setModalItem] = useState(null);
  const [submitting, setSubmitting] = useState({});

  useEffect(() => {
    let mounted = true;

    function updateDebug(patch) {
      try {
        if (typeof window === "undefined") return;
        window.__loopRatingDebug = {
          ...(window.__loopRatingDebug || {}),
          ...(patch || {}),
          platform: window.Capacitor?.getPlatform?.() || window.Capacitor?.platform || "web",
          isIosNative: isRatingNativeIos(),
          routeHint: getRatingRouteHint(),
          user: user ? { uid: user.uid || "", email: user.email || "", source: user.source || "" } : null,
          loadedAt: new Date().toISOString(),
        };
        window.__LOOP_RATING_DEBUG__ = () => window.__loopRatingDebug || {};
        window.__loopFindRatingBooking = (bookingId) => {
          const id = String(bookingId || "").trim();
          const debug = window.__loopRatingDebug || {};
          const all = Array.isArray(debug.all) ? debug.all : [];
          const visible = Array.isArray(debug.visible) ? debug.visible : [];
          return {
            id,
            inAll: all.find((x) => String(x.id || "") === id) || null,
            inVisible: visible.find((x) => String(x.id || "") === id) || null,
            similar: all.filter((x) => id && String(x.id || "").toLowerCase().includes(id.slice(0, 5).toLowerCase())).slice(0, 10),
            total: all.length,
            visibleTotal: visible.length,
          };
        };
      } catch (_) {}
    }

    async function fetchRouteHintBooking() {
      const hint = getRatingRouteHint();
      if (!hint.businessId || !hint.bookingId) return [];
      const path = `businesses/${hint.businessId}/bookings/${hint.bookingId}`;
      try {
        if (isRatingNativeIos() && typeof fsClient?.getDocByPath === "function") {
          const docData = await withRatingTimeout(
            fsClient.getDocByPath(path, { db: dbUsed }),
            RATING_IOS_QUERY_TIMEOUT_MS,
            null,
            `route-hint-booking:ios-rest:${hint.bookingId}`
          );
          const row = normalizeFsDocResult(docData, path, "route-hint-booking:ios-rest", hint.businessId);
          return row ? [row] : [];
        }
        const snap = await withRatingTimeout(getDoc(doc(dbUsed, "businesses", hint.businessId, "bookings", hint.bookingId)), RATING_QUERY_TIMEOUT_MS, null, `route-hint-booking:${hint.bookingId}`);
        const row = normalizeFsDocResult(snap, path, "route-hint-booking", hint.businessId);
        return row ? [row] : [];
      } catch (e) {
        console.warn("[CalificarServicios] route hint booking failed", e);
        return [];
      }
    }

    async function enrichBusinessNames(bookings) {
      const rows = Array.isArray(bookings) ? bookings : [];
      const ids = Array.from(new Set(
        rows
          .map((b) => getBookingBusinessId(b))
          .filter(Boolean)
          .map(String)
      )).slice(0, 40);

      if (!ids.length) {
        return rows.map((b) => ({
          ...b,
          businessId: getBookingBusinessId(b) || b.businessId || null,
          __businessLogo: ratingBusinessLogo(b),
          __businessArea: ratingBusinessArea(b),
        }));
      }

      const entries = await Promise.all(ids.map(async (businessId) => {
        try {
          const snap = isRatingNativeIos() && typeof fsClient?.getDocByPath === "function"
            ? await withRatingTimeout(fsClient.getDocByPath(`businesses/${businessId}`, { db: dbUsed }), RATING_IOS_QUERY_TIMEOUT_MS, null, `business-profile:ios-rest:${businessId}`)
            : await withRatingTimeout(getDoc(doc(dbUsed, "businesses", businessId)), RATING_QUERY_TIMEOUT_MS, null, `business-profile:${businessId}`);
          const data = typeof snap?.data === "function" ? (snap.exists() ? (snap.data() || {}) : {}) : (snap || {});
          const name = data.name || data.businessName || data.title || "Negocio";
          const logo = ratingPickLogoFrom(data);
          const area = (
            ratingCleanText(data.location) ||
            ratingCleanText(data.address) ||
            ratingCleanText(data.city) ||
            ratingCleanText(data.area) ||
            ""
          );
          return [businessId, { id: businessId, name, logo, area }];
        } catch (_) {
          return [businessId, { id: businessId, name: "Negocio", logo: "", area: "" }];
        }
      }));

      const profileMap = new Map(entries);
      return rows.map((b) => {
        const businessId = getBookingBusinessId(b);
        const profile = businessId ? (profileMap.get(String(businessId)) || {}) : {};
        return {
          ...b,
          businessId: businessId || b.businessId || null,
          __businessName: b.businessName || b.__businessName || profile.name || "Negocio",
          __businessLogo: ratingPickLogoFrom(b) || b.__businessLogo || profile.logo || "",
          __businessArea: b.__businessArea || ratingBusinessArea(b) || profile.area || "",
          __businessProfile: {
            ...(b.__businessProfile || {}),
            ...profile,
          },
        };
      });
    }

    async function loadBookings() {
      const loadStartedAt = Date.now();
      setBookingsError(null);

      if (!user) {
        if (mounted) {
          setUserBookings([]);
          setLoadingBookings(false);
        }
        return;
      }

      const uid = String(user.uid || "").trim();
      const email = String(user.email || "").trim();
      const emailLower = normalizeRatingEmail(email);

      const cached = readRatingCache(bookingCacheKey);
      if (cached && Array.isArray(cached.items)) {
        if (mounted) {
          setUserBookings(cached.items);
          setLoadingBookings(false);
          updateDebug({
            uid,
            email,
            emailLower,
            fromCache: true,
            cacheAgeMs: Date.now() - Number(cached.createdAt || Date.now()),
            loadMode: "session-cache-then-refresh",
            all: cached.items.map(summarizeRatingBookingForDebug),
          });
        }
      } else {
        setUserBookings([]);
        setLoadingBookings(true);
      }

      setRefreshingBookings(true);
      let currentRows = [];
      let hintRows = [];
      const publish = () => {
        if (!mounted) return;
        const rows = mergeBookingsByKey(currentRows, hintRows);
        setUserBookings(rows);
        if (rows.length) setLoadingBookings(false);
        return rows;
      };
      const hintPromise = fetchRouteHintBooking().then(rows => {
        hintRows = rows.filter(row => bookingMatchesRatingUser(row, user));
        if (hintRows.length) publish();
      });
      try {
        await loadRatingBookings({
          isActive: () => mounted,
          fetchPage: data => callCallableNativeSafe({
            functionName: "getUserBookingsPageV1", data, timeoutMs: 15000,
            requireAuth: true, requireAppCheck: false,
          }),
          onPage: rows => { currentRows = rows; publish(); },
        });
        await hintPromise;
        if (!mounted) return;
        const finalRows = publish() || [];
        writeRatingCache(bookingCacheKey, { items: finalRows });
        setLoadingBookings(false);
        setRefreshingBookings(false);
        updateDebug({ loadMode: "authenticated-paged", loadMs: Date.now() - loadStartedAt,
          all: finalRows.map(summarizeRatingBookingForDebug) });

      } catch (e) {
        if (!mounted) return;
        console.error("RatingTab bookings load failed", e);
        setBookingsError("Algunos servicios no se pudieron actualizar.");
      } finally {
        if (mounted) { setLoadingBookings(false); setRefreshingBookings(false); }
        if (!mounted) return;
        const enriched = await enrichBusinessNames(mergeBookingsByKey(currentRows, hintRows).filter(isBookingEligibleForRating));
        if (!mounted) return;
        const profiles = new Map(enriched.map(row => [row._path || row.id, row]));
        setUserBookings(previous => previous.map(row => ({
          ...row,
          __businessName: profiles.get(row._path || row.id)?.__businessName || row.__businessName,
          __businessLogo: profiles.get(row._path || row.id)?.__businessLogo || row.__businessLogo,
          __businessArea: profiles.get(row._path || row.id)?.__businessArea || row.__businessArea,
        })));

      }
    }

    loadBookings();
    return () => {
      mounted = false;
    };
  }, [user?.uid, user?.email, dbUsed, bookingCacheKey, bookingRetry]);

  useEffect(() => {
    let mounted = true;

    async function loadRatings() {
      setLoadingRatings(true);

      if (!user) {
        if (mounted) {
          setRatedBookingIds(new Set());
          setLoadingRatings(false);
        }
        return;
      }

      const cached = readRatingCache(ratedCacheKey);
      if (cached && Array.isArray(cached.ids)) {
        if (mounted) {
          setRatedBookingIds(new Set(cached.ids));
          setLoadingRatings(false);
        }
      }

      const uid = String(user.uid || "").trim();
      if (!uid) {
        if (mounted) setLoadingRatings(false);
        return;
      }

      try {
        const rated = new Set();
        if (!isRatingNativeIos()) {
          const qr = query(collectionGroup(dbUsed, "ratings"), where("userId", "==", uid), fbLimit(120));
          const rsnap = await withRatingTimeout(getDocs(qr), RATING_QUERY_TIMEOUT_MS, { docs: [] }, "ratings-cg");
          (rsnap?.docs || []).forEach((d) => {
            const data = d.data() || {};
            if (data.bookingId) rated.add(String(data.bookingId));
            if (data.bookingPath) rated.add(String(data.bookingPath));
          });
        }

        if (!mounted) return;
        setRatedBookingIds(rated);
        writeRatingCache(ratedCacheKey, { ids: Array.from(rated) });
      } catch (e) {
        if (!mounted) return;
        console.warn("RatingTab ratings query failed (non-fatal)", e);
        if (!cached) setRatedBookingIds(new Set());
      } finally {
        if (mounted) setLoadingRatings(false);
      }
    }

    loadRatings();
    return () => {
      mounted = false;
    };
  }, [user?.uid, user?.email, dbUsed, ratedCacheKey]);

  // No bloqueamos la pantalla esperando ratings: las reservas ya traen flags de rating y
  // el set de ratings se refresca en paralelo para filtrar sin hacer sentir lenta la página.
  const loading = loadingBookings;

  const items = useMemo(() => {
    const rated = ratedBookingIds || new Set();
    return (userBookings || [])
      .filter(isBookingEligibleForRating)
      .filter((b) => !rated.has(String(b.id)) && !rated.has(String(b._path || "")))
      .sort((a, b) => {
        const da = getBookingDateObject(a)?.getTime() || 0;
        const db = getBookingDateObject(b)?.getTime() || 0;
        return db - da;
      });
  }, [userBookings, ratedBookingIds]);

  useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.__loopRatingDebug) {
        window.__loopRatingDebug.visible = (items || []).map((b) => ({
          id: b.id || b.bookingId,
          businessId: getBookingBusinessId(b),
          customerEmail: b.customerEmail || b.userEmail || b.clientEmail || b.customerSnapshot?.email || b.customer?.email || "",
          status: b.status || "",
          bookingStatus: b.bookingStatus || b.bookingLifecycle?.bookingStatus || "",
          ratingStatus: getRatingBookingStatus(b),
          date: formatBookingDate(b),
        }));
        window.__loopRatingDebug.visibleCount = items.length;
        window.__loopRatingDebug.loadingRatings = loadingRatings;
      }
    } catch (_) {}
  }, [items, loadingRatings]);

  async function submitRating(item, rating, comment) {
    if (!user) return;
    const bookingId = item.id || item.bookingId;
    const businessId = getBookingBusinessId(item);
    setSubmitting((s) => ({ ...s, [bookingId]: true }));

    try {
      if (!businessId) throw new Error("booking.businessId missing");

      const payload = {
        businessId,
        bookingId: String(bookingId),
        rating: Number(rating),
        comment: String(comment || "").trim(),
      };

      let res = null;
      try {
        if (typeof window !== "undefined") {
          window.__loopRatingDebug = {
            ...(window.__loopRatingDebug || {}),
            lastSubmitAttempt: {
              ...payload,
              isIosNative: isRatingNativeIos(),
              mode: isRatingNativeIos() ? "ios-http" : "callable",
              startedAt: new Date().toISOString(),
            },
          };
        }
      } catch (_) {}

      if (isRatingNativeIos()) {
        try {
          res = await withRatingTimeout(submitRatingHttpIos(payload), 18000, null, "submitRating-http-ios");
          if (res && !res.data) res = { data: res };
        } catch (httpErr) {
          const message = normalizeSubmitRatingHttpError(httpErr);
          try {
            if (typeof window !== "undefined") {
              window.__loopRatingDebug = {
                ...(window.__loopRatingDebug || {}),
                lastSubmitError: { mode: "ios-http", message, raw: String(httpErr?.message || httpErr || "") },
              };
            }
          } catch (_) {}
          throw new Error(message || "No pudimos enviar la valoración. Revisa tu conexión e intenta nuevamente.");
        }
      } else {
        const fn = httpsCallable(getFunctions(firebaseApp, "us-central1"), "submitRating");
        res = await withRatingTimeout(fn(payload), 18000, null, "submitRating-callable");
      }

      if (!res) throw new Error("No pudimos enviar la valoración. Revisa tu conexión e intenta nuevamente.");

      try {
        if (typeof window !== "undefined") {
          window.__loopRatingDebug = {
            ...(window.__loopRatingDebug || {}),
            lastSubmitResult: { ok: true, mode: isRatingNativeIos() ? "ios-http" : "callable", ratingId: res?.data?.ratingId || res?.ratingId || null, finishedAt: new Date().toISOString() },
          };
        }
      } catch (_) {}

      setRatedBookingIds((prev) => {
        const next = new Set(prev || []);
        next.add(String(bookingId));
        if (item._path) next.add(String(item._path));
        writeRatingCache(ratedCacheKey, { ids: Array.from(next) });
        return next;
      });

      setUserBookings((prev) => {
        const nextRows = (prev || []).map((b) => {
          const sameBooking = String(b.id || b.bookingId || "") === String(bookingId) || (item._path && b._path === item._path);
          return sameBooking ? { ...b, ratingSubmitted: true, rated: true, ratingId: res?.data?.ratingId || true } : b;
        });
        writeRatingCache(bookingCacheKey, { items: nextRows });
        return nextRows;
      });

      setModalItem(null);
    } catch (err) {
      console.error("submitRating error", err);
      alert(err?.message || "Error enviando la valoración. Revisa la consola.");
    } finally {
      setSubmitting((s) => ({ ...s, [bookingId]: false }));
    }
  }

  return (
    <div className="rating-screen">
      <style>{styles}</style>

      <div className="rating-page">
        <div className="rating-hero">
          <div className="rating-kicker">Experiencias completadas</div>
          <h3 className="rating-title">Calificar servicios</h3>
          <p className="rating-copy">
            Aquí aparecen tus servicios completados. Puedes calificarlos desde 30 minutos después de terminar y hasta 7 días después.
          </p>
        </div>

        {loading && !bookingsError && <RatingSkeletonList count={4} />}

        {!loading && bookingsError && <div className="rating-refresh-notice" role="status">{bookingsError} <button type="button" className="rating-secondary-btn" onClick={() => setBookingRetry(n => n + 1)}>Reintentar</button></div>}
        {!loading && refreshingBookings && <p role="status">Actualizando tus servicios…</p>}

        {!loading && !refreshingBookings && !bookingsError && items.length === 0 && (
          <div className="rating-state">No tienes servicios por calificar por el momento.</div>
        )}

        {!loading && items.length > 0 && (
          <div className="rating-list rating-list-ready">
            {items.map((item) => {
              const businessName = ratingBusinessName(item);
              const serviceName = ratingServiceName(item);
              const businessArea = ratingBusinessArea(item);
              const durationLabel = ratingDurationLabel(item);
              const professionalName = ratingProfessionalName(item);
              const ratingDateLabel = formatBookingDate(item);
              return (
                <article key={item._path || item.id} className="rating-card rating-card-premium">
                  <RatingBusinessMediaMark item={item} businessName={businessName} />

                  <div className="rating-card-copy rating-card-main">
                    <div className="rating-card-title-row">
                      <div className="rating-card-title-stack">
                        <h4 className="rating-card-title">{businessName}</h4>
                        <p className="rating-card-service">{serviceName}</p>
                        {businessArea ? <p className="rating-business-area">{businessArea}</p> : null}
                      </div>
                      <span className="rating-status-pill">Por calificar</span>
                    </div>

                    <div className="rating-card-info-row">
                      {ratingDateLabel ? <span className="rating-card-date-chip">{ratingDateLabel}</span> : null}
                      {durationLabel ? <span className="rating-card-duration">{durationLabel}</span> : null}
                      <span className="rating-card-window">{formatRatingWindow(item)}</span>
                    </div>

                    <div className="rating-card-meta-grid">
                      <div>
                        <span>Fecha</span>
                        <strong>{formatBookingDate(item) || "Fecha no disponible"}</strong>
                      </div>
                      <div>
                        <span>Servicio</span>
                        <strong>{serviceName}</strong>
                      </div>
                      {professionalName ? (
                        <div>
                          <span>Profesional</span>
                          <strong>{professionalName}</strong>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="rating-card-actions">
                    <button
                      type="button"
                      onClick={() => setModalItem(item)}
                      className="rating-rate-btn"
                    >
                      Calificar
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {modalItem && (
          <RatingModal
            item={modalItem}
            submitting={!!submitting[modalItem.id || modalItem.bookingId]}
            onSubmit={submitRating}
            onClose={() => setModalItem(null)}
          />
        )}
      </div>
    </div>
  );
}
